"use client";

import { atomWithStorage } from "jotai/utils";
import type { Interval } from "./api";

// User chart customization. Persisted locally now; these same shapes sync server-side to
// pro_user_settings once wallet auth lands (Phase 2), so preferences follow the wallet.

export type ChartType = "candles" | "bars" | "line" | "area";

export interface CandleColors {
  up: string;
  down: string;
  /** Wick colors kept separate, traders often want muted wicks against solid bodies. */
  wickUp: string;
  wickDown: string;
}

/**
 * Presets, not just a color picker. "Classic" is the Dubu brand green vs destructive red.
 * "Colorblind-safe" matters: red/green is the worst possible encoding for ~8% of men, and
 * the buy/sell pair is the single most decision-critical color in the product, so blue vs
 * orange is offered as a first-class option, not an accessibility afterthought.
 * "Mono" suits users who read direction from candle position, not hue.
 */
export const CANDLE_PRESETS: Record<string, CandleColors> = {
  classic: {
    up: "#14a03a",
    down: "#dc3a3a",
    wickUp: "#14a03a",
    wickDown: "#dc3a3a",
  },
  "colorblind-safe": {
    up: "#0072b2",
    down: "#e69f00",
    wickUp: "#0072b2",
    wickDown: "#e69f00",
  },
  mono: {
    up: "#d4d4d4",
    down: "#525252",
    wickUp: "#d4d4d4",
    wickDown: "#525252",
  },
};

export const chartTypeAtom = atomWithStorage<ChartType>(
  "dubu-pro:chart-type",
  "candles"
);

export const intervalAtom = atomWithStorage<Interval>(
  "dubu-pro:interval",
  // 1h, not 1m, see DEFAULT_INTERVAL: this market trades ~5x/hour and a 1m chart is
  // almost entirely empty buckets.
  "1h"
);

export const candleColorsAtom = atomWithStorage<CandleColors>(
  "dubu-pro:candle-colors",
  CANDLE_PRESETS.classic
);

/**
 * Whether candle colors also drive the buy/sell UI (buttons, orderbook, PnL).
 * On by default so a user who recolors their chart doesn't end up with a green chart and
 * a red buy button.
 */
export const linkBuySellToChartAtom = atomWithStorage<boolean>(
  "dubu-pro:link-buysell",
  true
);
